import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { comparisonColumns } from '../../GscTools/columns';
import { reportsPosition } from '../../GscTools/metrics';
import { COMPARISON, formatPeriod } from '../../GscTools/period';

const FILTERS = ['all', 'gains', 'declines'];

/**
 * Block 05 — the pages whose clicks moved the most.
 *
 * Fetched with the rest of the report when a scope is applied, so the block fills itself rather
 * than offering a button that asks for the same thing the user has already asked for.
 *
 * The rows are a retrieved subset, not the property's full page list: Search Console withholds
 * rows below its own thresholds, and this report stops at a bounded number of them. The table
 * says so rather than letting its totals read as the property's totals — which is also why they
 * are never substituted for the headline figures above.
 */
class PageChanges extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.additionalBox = this.additionalBox.bind(this);
    }

    searchFunction(data, search) {
        if (!search) return data;
        return data.filter((item) => item.url?.toLowerCase().includes(search.toLowerCase()));
    }

    /**
     * All / Gains / Declines, filtered in memory — switching never costs another request.
     *
     * Drawn as the table-filter control the Categories table uses, which is what this product
     * reaches for whenever a table is filtered to one of a fixed set of views.
     */
    additionalBox() {
        const { t, filter, onFilter } = this.props;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <div className="table-additional-view-btn-groups">
                        {FILTERS.map((name) => (
                            <button
                                type="button"
                                key={name}
                                className={filter === name ? 'active' : ''}
                                aria-pressed={filter === name}
                                onClick={() => onFilter(name)}
                            >
                                {t(`tools.gsc.filters.${name}`)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    visibleRows() {
        const { rows, filter } = this.props;

        if (filter === 'gains') return rows.filter((row) => row.deltaClicks > 0);
        if (filter === 'declines') return rows.filter((row) => row.deltaClicks < 0);
        return rows;
    }

    render() {
        const { t, scope, status, error, period, shortened, stopReason, onLoadMore, loadingMore } =
            this.props;

        const disabled = scope.comparison === COMPARISON.NONE;
        const columns = comparisonColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            keyLabel: t('tools.gsc.columns.page'),
        });

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            {t('tools.search_overview.page_changes.title')}
                        </p>
                        <p className="gsc-tool-sub">
                            {t('tools.search_overview.page_changes.description')}
                            {!!period && <> · {formatPeriod(period)}</>}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {disabled && (
                        <div className="gsc-tool-banner">
                            {t('tools.search_overview.page_changes.needs_comparison')}
                        </div>
                    )}

                    {shortened && (
                        <div className="gsc-tool-banner">
                            {t('tools.search_overview.page_changes.shortened')}
                        </div>
                    )}

                    {/* Idle is the moment between the scope being applied and the rows being
                        asked for; it reads as the loading it is about to become. */}
                    {(status === 'idle' || status === 'loading') && !disabled && (
                        <Loading padding={20} />
                    )}

                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {status === 'done' && (
                        <>
                            {!!error && (
                                // A failure while loading more keeps the rows already joined, so
                                // the reason sits above them instead of replacing them.
                                <div className="gsc-tool-banner error">{error}</div>
                            )}

                            <DataTable
                                nodes={this.visibleRows()}
                                COLUMNS={columns}
                                sortFns={getSortKeys(columns)}
                                searchFunction={this.searchFunction}
                                additionalBox={this.additionalBox}
                                additionalStyles={setAdditionalStyles(columns)}
                                fileName={t('tools.search_overview.page_changes.title')}
                                customEmptyMessage={t('tools.search_overview.page_changes.no_rows')}
                                customContainerStyles="gsc-tool-key-table"
                                loadMore={onLoadMore}
                                loadMoreLoading={loadingMore}
                                showLoadMoreBtn={stopReason === 'more_available'}
                            />

                            <p className="gsc-tool-table-note">
                                {t(`tools.gsc.stop.${stopReason}`)}{' '}
                                {t('tools.search_overview.page_changes.subset')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(PageChanges);
